/**
 * Planner item schemas — one row in the student Planner feed.
 *
 * The item is a discriminated union on `kind`: `assignment` (Blackbaud work
 * synced into `Assignment`) and `personal` (a student-created item, private to
 * that student).
 *
 * Status uses the Planner's own lowercase vocabulary. The existing assignment
 * reads keep Blackbaud's uppercase vocabulary (`BlackbaudAssignmentStatusSchema`)
 * and map the resolved result back to it; only the Planner feed speaks this one.
 */

import { z } from 'zod'

/** Displayed status. `null` means Blackbaud reported none, so no label is shown (R11). */
export const PlannerStatusSchema = z.enum(['todo', 'in_progress', 'done'])
export type PlannerStatus = z.infer<typeof PlannerStatusSchema>

/**
 * Where the displayed status came from:
 * - `blackbaud` — Blackbaud's per-student status, as synced.
 * - `student` — the student's own in-app choice (slice 2).
 * - `graded` — grade evidence (a Graded status or recorded points), which always reads Done.
 */
export const PlannerStatusSourceSchema = z.enum(['blackbaud', 'student', 'graded'])
export type PlannerStatusSource = z.infer<typeof PlannerStatusSourceSchema>

/** Blackbaud's own per-student status, after collapsing duplicate rows. */
export const BlackbaudAssignmentStatusSchema = z.enum(['TODO', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE', 'GRADED'])
export type BlackbaudAssignmentStatus = z.infer<typeof BlackbaudAssignmentStatusSchema>

export const PlannerAssignmentItemSchema = z.object({
  kind: z.literal('assignment'),
  /** Assignment publicId. */
  id: z.string(),
  /** Display text of the assignment name (Blackbaud sends HTML). */
  title: z.string(),
  /** The section the student is enrolled in, never the assignment's first linked section. */
  section: z.object({
    id: z.string(),
    name: z.string()
  }),
  /**
   * Effective due instant (UTC ISO): the enrolled section's own due date, else
   * the assignment's. Clients bucket it into days in the feed's `timezone`.
   */
  dueAt: z.string().datetime().nullable(),
  /** False when the due time is 23:59 school-local, which Blackbaud uses for "due that day". */
  hasDueTime: z.boolean(),
  status: PlannerStatusSchema.nullable(),
  /** Null exactly when `status` is null. */
  statusSource: PlannerStatusSourceSchema.nullable(),
  /** Blackbaud flagged the work missing. Outranks grade evidence (R24). */
  isMissing: z.boolean(),
  blackbaudStatus: BlackbaudAssignmentStatusSchema.nullable(),
  /** Server-decided test flag: marked major, or a test/quiz/exam-like type. */
  isTestLike: z.boolean(),
  /** Blackbaud's late flag. */
  isLate: z.boolean()
})
export type PlannerAssignmentItem = z.infer<typeof PlannerAssignmentItemSchema>

/**
 * A student's own personal item (R17–R21). A date and a time range put it on
 * the schedule; a date alone makes it a to-do due that day; no date files it
 * under No date (R18).
 */
export const PlannerPersonalItemSchema = z.object({
  kind: z.literal('personal'),
  /** The client-generated publicId the item was created with. */
  id: z.string(),
  title: z.string(),
  /** An optional class the student is enrolled in. */
  section: z
    .object({
      id: z.string(),
      name: z.string()
    })
    .nullable(),
  /** School-local calendar day (`YYYY-MM-DD`), never timezone-shifted (KTD4). */
  date: z.iso.date().nullable(),
  /** Start instant (UTC ISO) of the school-local time range; set together with `endAt`. */
  startAt: z.string().datetime().nullable(),
  /** End instant (UTC ISO); always after `startAt` and on the same school-local day. */
  endAt: z.string().datetime().nullable(),
  note: z.string().nullable(),
  /** Always set: a personal item has no Blackbaud status to fall back on. */
  status: PlannerStatusSchema
})
export type PlannerPersonalItem = z.infer<typeof PlannerPersonalItemSchema>

export const PlannerItemSchema = z.discriminatedUnion('kind', [PlannerAssignmentItemSchema, PlannerPersonalItemSchema])
export type PlannerItem = z.infer<typeof PlannerItemSchema>
